import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { cssVariables } from '@env/css-vars';
import { environment } from '@env/environment';
import cssVars from 'css-vars-ponyfill';
import { assign } from 'lodash';
import { combineLatest, forkJoin, Observable } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { AzureBlobStorageService } from './shared/services/api/azure-blob-storage.service';
import { ConfigurationService } from './shared/services/api/configuration.service';
import { AppInsightsServiceDirective } from './shared/services/app-insights.service';
import { AuthService } from './shared/services/auth.service';
import { UserMunicipalitiesService } from './shared/services/user-municipalities.service';

@Injectable({
  providedIn: 'root',
})
export class AppInitService {
  constructor(
    private configurationService: ConfigurationService,
    private ngZone: NgZone,
    private metaService: Meta,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private authService: AuthService,
    private blobStorageService: AzureBlobStorageService,
    private appInsightsServiceDirective: AppInsightsServiceDirective,
    public http: HttpClient
  ) {}

  init() {
    if (environment.environment === 'production') {
      this.metaService.addTags([{ name: 'google', content: 'notranslate' }]);
      this.metaService.removeTag('name=robots');
    }
    return new Promise<void>((resolve, reject) => {
      this.loadPortalSettings()
        .pipe(
          mergeMap(() => {
            this.appInsightsServiceDirective.initialize();

            if (this.authService.appToken) {
              return combineLatest([
                this.loadCssVars(this.getFileLocation('variables.css')),
                this.loadFavicon(),
                this.userMunicipalitiesService.initMunicipalities(),
                this.userMunicipalitiesService.initRegions(),
              ]).toPromise();
            } else {
              return forkJoin([this.loadCssVars(this.getFileLocation('variables.css')), this.loadFavicon()]).toPromise();
            }
          })
        )
        .subscribe(() => resolve());
    });
  }

  loadPortalSettings() {
    return this.configurationService.getPortalSettings().pipe(tap((config) => assign(environment, config)));
  }

  loadFavicon() {
    return new Observable<any>((observer) => {
      const head = document.getElementsByTagName('head')[0];
      const link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/x-icon';
      link.href = this.getFileLocation('favicon.ico');
      head.appendChild(link);
      observer.next({});
      observer.complete();
    });
  }

  loadCss(href: string) {
    return new Observable<any>((observer) => {
      const head = document.getElementsByTagName('head')[0];
      const link = document.createElement('link');
      link.id = 'dsocss';
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = href;
      link.media = 'all';
      head.appendChild(link);
      observer.next({});
      observer.complete();
    });
  }

  loadCssVars(href: string) {
    return this.loadCss(href).pipe(
      mergeMap(() => {
        return new Observable<any>((observer) => {
          const promise = new Promise<void>((resolve, reject) => {
            this.ngZone.runOutsideAngular(() => {
              cssVars({
                onlyLegacy: false,
                watch: true,
                onFinally: () => {
                  resolve();
                },
                onComplete: (cssText, styleElms, vars, benchmark) => {
                  assign(cssVariables, vars);
                },
              });
            });
          });

          promise.then(() => {
            observer.next({});
            observer.complete();
          });
        });
      })
    );
  }

  getFileLocation(fileName: string) {
    if (window.location.hostname === 'localhost') {
      return `/DsoStaticFiles/${environment.tenant}/${fileName}`;
    } else {
      return this.blobStorageService.getBlobFilePath(`${environment.tenant}/assets/${fileName}`);
    }
  }
}
